import { useFeatureFlag } from "@vericus/cadmus-common";

import { useParams } from "react-router-dom";

import { QuestionType } from "@/generated/graphql";
import { useAssessmentSettingsFragment } from "@/graphql/assessment-selectors";
import { useAnswerBlock } from "@/graphql/hooks";

/**
 * Whether the referencing editor and footnotes should be available for
 * the Extended (`QuestionType.Extended`) question that owns `answerBlockId`.
 * Returns `false` for any other question type.
 *
 * Three gates that must all allow it:
 *  - the institution feature flag `references_footnotes_in_eaq`,
 *  - the assessment allows referencing (`referencingStyleEnabled`),
 *  - the question's per-field `referencingEnabled` toggle is on.
 */
export function useReferencingFootnotesEnabledForExtendedQuestion(
  answerBlockId: string | null
): boolean {
  const { assessmentId = "" } = useParams<{ assessmentId: string }>();
  const settings = useAssessmentSettingsFragment(assessmentId);
  const enabledForInstitution = useFeatureFlag("references_footnotes_in_eaq");

  const { data } = useAnswerBlock(answerBlockId);

  const question = data?.question;
  const interaction = question?.body?.fields?.[0]?.interaction;
  const enabledForQuestion =
    question?.questionType === QuestionType.Extended &&
    interaction?.__typename === "ExtendedInteraction" &&
    interaction.referencingEnabled === true;

  return (
    enabledForInstitution &&
    settings?.referencingStyleEnabled === true &&
    enabledForQuestion
  );
}
